import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import axios from 'axios';

import { DEFAULT_RETRY_DELAY, DEFAULT_RETRY_NUMBERS } from '../../../config/query-client';
import { clinicalInfoQueryKey } from './consts';
import useDiagnosisService from './diagnosis';
import useFallService from './fall';
import useDeviceAdjustmentService from './medicalDevice';
import useSurgeryService from './surgery';
import type { IClinicalInfo } from './types';

export default function useClinicalInfoService(patientUuid: string, readsRecords = true) {
  const queryClient = useQueryClient();

  const result = useQuery<IClinicalInfo>({
    queryKey: clinicalInfoQueryKey(patientUuid),
    retry: DEFAULT_RETRY_NUMBERS,
    retryDelay: DEFAULT_RETRY_DELAY,
    enabled: !!patientUuid && readsRecords,
  });

  const updateAction = useMutation({
    mutationFn: async (data: Partial<IClinicalInfo>) => {
      await axios.post(`/api/camel/patient_profile/clinical_info/${patientUuid}`, data);
      if (patientUuid) await invalidatePatientQuery(patientUuid);
      result.refetch();
    },
  });

  const invalidatePatientQuery = async (patientUuid: string) => {
    await queryClient.invalidateQueries({
      queryKey: ['camel', 'patients', patientUuid],
    });
  };

  const diagnosis = useDiagnosisService();
  const surgery = useSurgeryService();
  const medicalDevice = useDeviceAdjustmentService();
  const fall = useFallService(patientUuid);

  return {
    refetch: result.refetch,
    data: result?.data ?? ({} as IClinicalInfo),
    diagnosis,
    surgery,
    medicalDevice,
    fall,
    isLoading: !!patientUuid && result?.isLoading,
    errorState: {
      isError: result?.isError,
      error: (result?.error as AxiosError)?.response?.status,
    },
    isFetching: result?.isFetching,
    updateClinicalInfo: updateAction.mutateAsync,
  };
}
